<template>
  <form class="vehicle-lookup-form" @submit.prevent="onLookup">
    <p>All fields are required.</p>

    <!-- Plate Number -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isPlateValid }">
      <label :for="plateField">Plate number</label>
      <input class="plate" type="text" maxlength="7" :id="plateField" :aria-describedby="`${plateField}Desc`"
             v-model.lazy.trim="myPlateNumber" aria-required="true" />
      <p v-if="!isPlateValid" class="app-error-message">{{ plateError }}</p>
      <p :id="`${plateField}Desc`" class="sr-only">{{ getFieldDescription(`${plateField}`) }}</p>
    </div>

    <!-- Registration Type -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isTypeValid }">
      <label :for="typeField">Registration type</label>
      <select class="app-custom-select" :id="typeField" :aria-describedby="`${typeField}Desc`"
              v-model.lazy="myRegistrationType" aria-required="true">
        <option :value="{ code: 'none' }">Select a type</option>
        <option v-for="type in registrationTypes" :value="{ code: type.code, description: type.description }" :key="type.code">
          {{ type.description }}
        </option>
      </select>
      <p v-if="!isTypeValid" class="app-error-message">{{ typeError }}</p>
      <p :id="`${typeField}Desc`" class="sr-only">{{ getFieldDescription(`${typeField}`) }}</p>
    </div>

    <!-- Last 6 VIN Digits -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': !isVinValid }">
      <label :for="vinField">Last six digits of VIN</label>
      <input class="vin" type="text" maxlength="6" :id="vinField" :aria-describedby="`${vinField}Desc`"
             v-model.lazy.trim="myLastVin" aria-required="true" />
      <p v-if="!isVinValid" class="app-error-message">{{ vinError }}</p>
      <p :id="`${vinField}Desc`" class="sr-only">{{ getFieldDescription(`${vinField}`) }}</p>
    </div>

    <!-- Certified Owner -->
    <div class="app-input-group __extend" :class="{ 'app-invalid-group': (isCertified !== undefined && !isCertified) }">
      <div class="app-checker-group" style="padding-top: 0.75em;">
        <input type="checkbox" :id="certifyField" :aria-describedby="`${certifyField}Desc`"
               @click="myCertifyCheck = !myCertifyCheck" aria-required="true">
        <label :for="certifyField">
          I certify that I am the registered owner of the vehicle identified by the information above,
          or I am authorized by the registered owner to check the registration status.
        </label>
      </div>
      <p v-if="!isCertified" class="app-error-message">{{ certifyError }}</p>
      <p :id="`${certifyField}Desc`" class="sr-only">{{ getFieldDescription(`${certifyField}`) }}</p>
    </div>

    <BaseButtonGroup>
      <template #default>
        <button class="app-btn-primary">
          Look up
        </button>
      </template>
    </BaseButtonGroup>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as COMMONS from '../../../store/_commons'
import vu from '@/utils/validationUtils'
import BaseButtonGroup from '@/components/ui/BaseButtonGroup'


export default {
  name: "LookupForm",
  components: {
    BaseButtonGroup,
  },
  data() {
    return {
      // labels
      plateField: 'plateNumber',
      typeField: 'registrationType',
      vinField: 'vinNumberLastSix',
      certifyField: 'certified',

      // input fields
      inputPattern: /^[a-zA-Z0-9]*$/,
      myPlateNumber: '',
      myRegistrationType: { code: 'none' },
      myLastVin: '',
      myCertifyCheck: false,
      myTmp: {},

      // field validity
      isPlateValid: true,
      isTypeValid: true,
      isVinValid: true,
      isCertified: undefined,
    };
  },
  computed: {
    ...mapGetters({
      lookupInfo: COMMONS.VEHICLE_LOOKUP_INFO,
    }),
    plateError() {
      if (!this.isPlateValid) {
        return this.getFieldErrorMessage(this.plateField);
      }

      return '';
    },
    typeError() {
      if (!this.isTypeValid) {
        return this.getFieldErrorMessage(this.typeField);
      }

      return '';
    },
    vinError() {
      if (!this.isVinValid) {
        return this.getFieldErrorMessage(this.vinField);
      }

      return '';
    },
    certifyError() {
      // must equal false (so, must be defined)
      if (this.isCertified === false) {
        return this.getFieldErrorMessage(this.certifyField);
      }

      return '';
    },
    /**
     * Error codes and messages mapped from BA's spreadsheet.
     * Used for generating form validation messages and 508-compliant field descriptions.
     *  - Validation (error) messages are per _code_ based on user input. A field can have more than one codes.
     *  - Accessible field description is per _field_.
     * @returns {({ref: string, message: string}|...)[]}
     */
    validationMessages() {
      return [
        { ref: this.plateField, message: 'Plate Number is required. ' +
              'Acceptable characters are letters or numbers. Please do not include dashes or spaces.' },
        { ref: this.typeField, message: 'Registration type is required.' },
        { ref: this.vinField, message: 'Last six digits of VIN are required, you must enter all six digits. ' +
              'Acceptable characters are letters or numbers.' },
        { ref: this.certifyField, message: 'Please attest that you are the registered owner and are authorized ' +
              'to check the registration status.' },
        { ref: 'registrationNotFound', message: 'No registration was found matching the information provided. ' +
              'Please make sure you are providing information exactly as it appears on the registration.' }
      ]
    },
    registrationTypes() {
      return this.lookupInfo.allRegistrationTypes;
    },
  },
  created() {
    this.getTypeList();
  },
  methods: {
    ...mapActions({
      getTypeList: COMMONS.VEHICLE_GET_TYPE_LIST,
      setData: COMMONS.VEHICLE_SET_DATA,
    }),
    getFieldErrorMessage(ref, code=undefined) {
      return vu.getFieldValidationMessage(this.validationMessages, ref, code);
    },
    getFieldDescription(ref) {
      return vu.getFieldAccessibleDescription(this.validationMessages, ref);
    },
    onLookup() {
      this.validate();

      // store user inputs and call lookup api
      if (this.isPlateValid && this.isTypeValid && this.isVinValid && this.isCertified) {
        this.setData({
          plateNumber: this.myPlateNumber.toUpperCase(),
          registrationType: this.myRegistrationType,
          vinNumberLastSix: this.myLastVin.toUpperCase(),
          isVehicleOwnerConsented: this.isCertified,
          runLookup: true,
        });
      }
    },
    validatePlate() {
      const val = this.myPlateNumber;
      this.isPlateValid = ((val.length > 0 && val.length <= 7) && this.inputPattern.test(val));
    },
    validateType() {
      this.isTypeValid = (this.myRegistrationType.code !== 'none');
    },
    validateVin() {
      const val = this.myLastVin;
      this.isVinValid = (val.length === 6 && this.inputPattern.test(val));
    },
    validateCertifyCheck() {
      this.isCertified = (this.myCertifyCheck === undefined ? false : this.myCertifyCheck);
    },
    /**
     * Validate all fields onSubmit. No immediate validations required during user input.
     */
    validate() {
      this.validatePlate();
      this.validateType();
      this.validateVin();
      this.validateCertifyCheck();
    },
  }
}
</script>

<style lang="scss" scoped>
.vehicle-lookup-form {
  margin-top: 1em;
  input[type="text"] {
    text-transform: uppercase;
  }
  .plate {
    width: 145px;
  }
  .vin {
    width: 125px;
  }
}
</style>
